import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { useDropdownDismiss, useAssociationSearch, useInfiniteScrollLoader } from './hooks.js';
import { ExpandIcon, CloseIcon } from './icons.jsx';
import { readApiPayload, deriveSchemaUrlFromReactPageUrl } from './utils.js';
import { App } from './App.jsx';
import { RenderErrorBoundary } from './RenderErrorBoundary.jsx';

export function getAjaxSelectMode(selectEl) {
  return selectEl.getAttribute('data-ajax-mode') || (selectEl.multiple ? 'multiple' : 'single');
}

function readAjaxIdList(rawValue) {
  if (!rawValue) {
    return [];
  }

  let value = rawValue;
  try {
    value = JSON.parse(rawValue);
  } catch (error) {
    value = rawValue.split(',');
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(new Set(value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)));
}

export function getAjaxSelectConfig(selectEl) {
  const rawExtra = selectEl.getAttribute('data-ajax-extra') || '';
  const rawExclude = selectEl.getAttribute('data-ajax-exclude') || '';
  const rawDescFields = selectEl.getAttribute('data-ajax-desc-fields') || '';
  const rawFieldValues = selectEl.getAttribute('data-ajax-field-values') || '';
  const rawTextField = selectEl.getAttribute('data-ajax-text-field') || '';

  return {
    manager: selectEl.getAttribute('data-ajax-manager'),
    method: selectEl.getAttribute('data-ajax-method') || 'findBy',
    pageSize: parseInt(selectEl.getAttribute('data-ajax-n') || '50', 10),
    initialPageSize: parseInt(selectEl.getAttribute('data-ajax-initial-n') || '20', 10),
    minimumInputLength: parseInt(selectEl.getAttribute('data-ajax-min-length') || '0', 10),
    baseUrl: window.drupalSettings?.path?.baseUrl ?? '/',
    placeholder: selectEl.options[0]?.text || '-- Sélectionner --',
    searchPlaceholder: selectEl.getAttribute('data-ajax-search-placeholder') || 'Rechercher…',
    mode: getAjaxSelectMode(selectEl),
    displayMode: selectEl.getAttribute('data-ajax-display') || 'dropdown',
    extra: rawExtra,
    exclude: rawExclude,
    descFields: rawDescFields,
    fieldValues: rawFieldValues,
    textField: rawTextField,
  };
}

function readSelectedItemMetadata(selectEl) {
  const rawItems = selectEl.getAttribute('data-ajax-selected-items') || '';
  if (!rawItems) {
    return new Map();
  }

  try {
    const items = JSON.parse(rawItems);
    if (!Array.isArray(items)) {
      return new Map();
    }

    return new Map(items
      .filter((item) => item && item.id != null)
      .map((item) => {
        const id = String(item.id);
        return [id, {
          id,
          text: item.text || '',
          description: item.description || '',
        }];
      }));
  } catch (error) {
    return new Map();
  }
}

export function readSelectedOptions(selectEl) {
  const metadata = readSelectedItemMetadata(selectEl);

  return Array.from(selectEl.options)
    .filter((option) => option.selected && option.value)
    .map((option) => {
      const id = String(option.value);
      const item = metadata.get(id);

      return {
        id,
        text: item?.text || option.text,
        description: item?.description || '',
      };
    });
}

export function ensureSelectOption(selectEl, item) {
  const itemId = String(item.id);
  let option = Array.from(selectEl.options).find((candidate) => String(candidate.value) === itemId);
  if (!option) {
    option = new Option(item.text, itemId, false, false);
    selectEl.appendChild(option);
  }
  option.text = item.text;
  option.value = itemId;
  // Persist field_values from the AJAX response so consumers (e.g. amf-table.js
  // associate handler) can populate summary table cells from the selected item.
  if (item.field_values && typeof item.field_values === 'object') {
    try {
      option.dataset.fieldValues = JSON.stringify(item.field_values);
    } catch (_) {}
  }
  return option;
}

export function dispatchSelectChange(selectEl) {
  selectEl.dispatchEvent(new Event('change', { bubbles: true }));
}

function syncSelectedItemMetadata(selectEl, items) {
  const normalizedItems = (items || [])
    .filter((item) => item?.id != null)
    .map((item) => {
      const out = {
        id: String(item.id),
        text: item.text || '',
        description: item.description || '',
      };
      if (item.field_values && typeof item.field_values === 'object') {
        out.field_values = item.field_values;
      }
      return out;
    });

  selectEl.setAttribute('data-ajax-selected-items', JSON.stringify(normalizedItems));
}

export function syncSingleSelect(selectEl, item) {
  Array.from(selectEl.options).forEach((option, index) => {
    option.selected = !item && index === 0;
  });

  if (item?.id) {
    const option = ensureSelectOption(selectEl, item);
    option.selected = true;
    selectEl.value = String(item.id);
  } else {
    selectEl.value = '';
  }

  syncSelectedItemMetadata(selectEl, item ? [item] : []);
  dispatchSelectChange(selectEl);
}

function formatAssociationOption(item) {
  const rawText = (item?.text || '').trim();
  const rawDescription = (item?.description || '').trim();

  if (rawDescription) {
    return {
      main: rawText,
      meta: rawDescription,
    };
  }

  const splitMatch = rawText.match(/^(.+?)\s[-–—]\s(.+)$/);
  if (splitMatch) {
    return {
      main: splitMatch[1].trim(),
      meta: splitMatch[2].trim(),
    };
  }

  return {
    main: rawText,
    meta: '',
  };
}

function AssociationOptionLabel({ item }) {
  const { main, meta } = formatAssociationOption(item);

  if (!meta) {
    return main;
  }

  return (
    <span className="ras-option-label">
      <span className="ras-option-label__main">{main}</span>
      <span className="ras-option-label__meta">{meta}</span>
    </span>
  );
}

export function syncMultiSelect(selectEl, items) {
  const selectedIds = new Set((items || []).map((item) => String(item.id)));
  (items || []).forEach((item) => ensureSelectOption(selectEl, item));

  Array.from(selectEl.options).forEach((option) => {
    if (!option.value) {
      option.selected = false;
      return;
    }
    option.selected = selectedIds.has(String(option.value));
  });

  syncSelectedItemMetadata(selectEl, items || []);
  dispatchSelectChange(selectEl);
}

export function ReactAjaxSingleSelect({ selectEl }) {
  const baseConfig = useMemo(() => getAjaxSelectConfig(selectEl), [selectEl]);
  const isInlineMode = baseConfig.displayMode === 'inline';

  // FilteredAssociationOneField: track parent field value(s) — one or two parents supported.
  const dependsOn = selectEl.getAttribute('data-depends-on');
  const dependsParam = selectEl.getAttribute('data-depends-param');
  const dependsOn2 = selectEl.getAttribute('data-depends-on-2');
  const dependsParam2 = selectEl.getAttribute('data-depends-param-2');

  // Word-boundary match: look for an exact name OR a name ending with
  // `[fieldName]`. Prevents `grade` from matching `rhu_grade` under the old
  // `[name*="..."]` substring selector.
  const findSelectByField = (fieldName) => {
    if (!fieldName) return null;
    const form = selectEl.closest('form');
    if (!form) return null;
    const selects = form.querySelectorAll('select');
    for (let i = 0; i < selects.length; i++) {
      const n = selects[i].name || '';
      if (n === fieldName || n.endsWith(`[${fieldName}]`)) {
        return selects[i];
      }
    }
    return null;
  };

  const readParentValue = (fieldName) => findSelectByField(fieldName)?.value || null;

  const [parentValue, setParentValue] = useState(() => readParentValue(dependsOn));
  const [parentValue2, setParentValue2] = useState(() => readParentValue(dependsOn2));

  useEffect(() => {
    if (!dependsOn) return undefined;
    const parentEl = findSelectByField(dependsOn);
    if (!parentEl) return undefined;
    const handler = () => setParentValue(parentEl.value || null);
    parentEl.addEventListener('change', handler);
    return () => parentEl.removeEventListener('change', handler);
  }, [selectEl, dependsOn]);

  useEffect(() => {
    if (!dependsOn2) return undefined;
    const parentEl = findSelectByField(dependsOn2);
    if (!parentEl) return undefined;
    const handler = () => setParentValue2(parentEl.value || null);
    parentEl.addEventListener('change', handler);
    return () => parentEl.removeEventListener('change', handler);
  }, [selectEl, dependsOn2]);

  // Build config with dynamic extra from parent value(s)
  const config = useMemo(() => {
    const hasP1 = dependsParam && parentValue;
    const hasP2 = dependsParam2 && parentValue2;
    if (!hasP1 && !hasP2) return baseConfig;
    const existingExtra = baseConfig.extra ? JSON.parse(baseConfig.extra) : {};
    if (hasP1) existingExtra[dependsParam] = parseInt(parentValue, 10);
    if (hasP2) existingExtra[dependsParam2] = parseInt(parentValue2, 10);
    return { ...baseConfig, extra: JSON.stringify(existingExtra) };
  }, [baseConfig, dependsParam, parentValue, dependsParam2, parentValue2]);

  const [isOpen, setIsOpen] = useState(() => isInlineMode);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => readSelectedOptions(selectEl)[0] || null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const resultsSentinelRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const effectiveIsOpen = isInlineMode || isOpen;
  const excludedIds = useMemo(() => readAjaxIdList(config.exclude), [config.exclude]);
  const { loading, results, setResults, hasMore, loadMore } = useAssociationSearch({
    config,
    isOpen: effectiveIsOpen,
    search,
    allowEmptySearch: config.initialPageSize > 0,
    minimumInputLength: Math.max(config.minimumInputLength, 0),
    initialPageSize: config.initialPageSize,
  });

  // Reset child selection when any parent changes
  const isFirstParentRender = useRef(true);
  useEffect(() => {
    if (isFirstParentRender.current) {
      isFirstParentRender.current = false;
      return;
    }
    setSelected(null);
    syncSingleSelect(selectEl, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentValue, parentValue2]);

  useDropdownDismiss(containerRef, () => {
    setIsOpen(false);
    setSearch('');
  }, !isInlineMode);

  useEffect(() => {
    if (effectiveIsOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [effectiveIsOpen]);

  useInfiniteScrollLoader({
    containerRef: resultsRef,
    sentinelRef: resultsSentinelRef,
    onLoadMore: loadMore,
    enabled: effectiveIsOpen && hasMore,
  });

  // Disable field when any declared parent has no value
  const isDisabledByParent =
    (Boolean(dependsOn) && !parentValue) ||
    (Boolean(dependsOn2) && !parentValue2);

  const open = () => {
    if (isInlineMode || isDisabledByParent) {
      return;
    }
    setIsOpen(true);
    setSearch('');
    setResults([]);
  };

  const pick = (item) => {
    setSelected(item);
    syncSingleSelect(selectEl, item);
    if (!isInlineMode) {
      setIsOpen(false);
      setSearch('');
      setResults([]);
    }
  };

  const minimumInputLength = Math.max(config.minimumInputLength, 1);
  const hasValue = Boolean(selected?.id);
  const trimmedSearch = search.trim();
  const isSearchTooShort = !loading && results.length === 0 && trimmedSearch.length > 0 && trimmedSearch.length < minimumInputLength;
  const emptyMessage = !isSearchTooShort && !loading && results.length === 0
    ? (
      trimmedSearch.length === 0
        ? (excludedIds.length > 0
            ? 'Aucun autre élément disponible.'
            : 'Aucun élément disponible.')
        : 'Aucun résultat'
    )
    : '';
  const isEmptyState = !loading && !isSearchTooShort && results.length === 0;
  const resultsMarkup = (
    <div className={`ras-results${isInlineMode ? ' ras-results--inline' : ''}${isEmptyState ? ' ras-results--empty' : ''}`} ref={resultsRef}>
      {results.length === 0 && loading && <div className="ras-msg">Recherche…</div>}
      {!loading && isSearchTooShort && (
        <div className="ras-msg">
          {`Saisir au moins ${minimumInputLength} caractère${minimumInputLength > 1 ? 's' : ''}…`}
        </div>
      )}
      {!loading && !isSearchTooShort && results.length === 0 && <div className="ras-msg">{emptyMessage}</div>}
      {results.map((item) => (
        <div
          key={item.id}
          className={`ras-option${selected?.id == item.id ? ' ras-option--selected' : ''}`}
          role="option"
          aria-selected={selected?.id == item.id}
          onMouseDown={() => pick(item)}
        >
          <AssociationOptionLabel item={item} />
        </div>
      ))}
      {hasMore && <div ref={resultsSentinelRef} className="ras-load-sentinel" aria-hidden="true" />}
      {results.length > 0 && loading && <div className="ras-msg ras-msg--load-more">Chargement…</div>}
    </div>
  );

  if (isInlineMode) {
    // Cascade-aware multistep rendering:
    //   - Upcoming step (parent not picked) → collapsed "locked" row.
    //   - Picked step → compact pill with ✓, clickable to re-open.
    //   - Active step → full search UI with step header.
    const stepIndexAttr = selectEl.getAttribute('data-cascade-step-index');
    const stepTotalAttr = selectEl.getAttribute('data-cascade-step-total');
    const stepLabel = selectEl.getAttribute('data-cascade-step-label') || '';
    const stepIndex = stepIndexAttr !== null ? parseInt(stepIndexAttr, 10) + 1 : null;
    const stepTotal = stepTotalAttr !== null ? parseInt(stepTotalAttr, 10) : null;
    const isCascadeStep = stepIndex !== null && stepTotal !== null;
    const stepNumber = isCascadeStep ? (
      <span className="ras-step__num">{stepIndex}</span>
    ) : null;

    if (isDisabledByParent) {
      return (
        <div className="ras-inline-picker ras-inline-picker--locked" ref={containerRef}>
          <div className="ras-step ras-step--locked">
            {stepNumber}
            <span className="ras-step__label">{stepLabel}</span>
          </div>
        </div>
      );
    }

    if (hasValue) {
      return (
        <div className="ras-inline-picker ras-inline-picker--picked" ref={containerRef}>
          <button
            type="button"
            className="ras-step ras-step--picked"
            onClick={() => {
              setSelected(null);
              syncSingleSelect(selectEl, null);
              setSearch('');
            }}
            aria-label={`Modifier ${stepLabel || 'la sélection'}`}
          >
            {stepNumber}
            <span className="ras-step__label">{stepLabel}</span>
            <span className="ras-step__value">
              <AssociationOptionLabel item={selected} />
            </span>
            <span className="ras-step__clear" aria-hidden="true">
              <CloseIcon />
            </span>
          </button>
        </div>
      );
    }

    return (
      <div className="ras-inline-picker ras-inline-picker--active" ref={containerRef}>
        {isCascadeStep && (
          <div className="ras-step ras-step--active">
            {stepNumber}
            <span className="ras-step__label">{stepLabel}</span>
          </div>
        )}
        <div className="ras-search ras-search--inline">
          <input
            ref={inputRef}
            type="text"
            className="ras-search__input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={config.searchPlaceholder}
            aria-autocomplete="list"
          />
        </div>
        {resultsMarkup}
      </div>
    );
  }

  return (
    <div className={`ras-wrap${isOpen ? ' ras-wrap--open' : ''}`} ref={containerRef}>
      <button
        type="button"
        className={`ras-value${hasValue ? '' : ' ras-value--placeholder'}${selected?.description ? ' ras-value--stacked' : ''}${isDisabledByParent ? ' ras-value--disabled' : ''}`}
        onClick={open}
        disabled={isDisabledByParent}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="ras-value__label">
          {hasValue ? <AssociationOptionLabel item={selected} /> : config.placeholder}
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="ras-dropdown"
            role="listbox"
            initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scaleY: 0.96 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scaleY: 0.96 }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: 'top' }}
          >
            <div className="ras-search">
              <input
                ref={inputRef}
                type="text"
                className="ras-search__input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher…"
                aria-autocomplete="list"
              />
            </div>
            {resultsMarkup}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ReactAjaxMultiSelect({ selectEl }) {
  const config = useMemo(() => getAjaxSelectConfig(selectEl), [selectEl]);
  const [selected, setSelected] = useState(() => readSelectedOptions(selectEl));
  const [isSelectionPanelOpen, setIsSelectionPanelOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [detailState, setDetailState] = useState(null);
  const [modalSearch, setModalSearch] = useState('');
  const modalInputRef = useRef(null);
  const modalResultsRef = useRef(null);
  const modalResultsSentinelRef = useRef(null);
  const detailTriggerRef = useRef(null);
  const detailCacheRef = useRef(new Map());
  const detailRequestIdRef = useRef(0);
  const shouldReduceMotion = useReducedMotion();
  const { loading, results, hasMore: hasMoreModal, loadMore: loadMoreModal } = useAssociationSearch({
    config,
    isOpen: isAddModalOpen,
    search: modalSearch,
    allowEmptySearch: true,
    minimumInputLength: 1,
  });
  const selectedIds = useMemo(
    () => new Set(selected.map((item) => String(item.id))),
    [selected],
  );
  const filteredSelected = useMemo(() => {
    const normalizedSearch = selectedSearch.trim().toLocaleLowerCase();
    if (!normalizedSearch) {
      return selected;
    }

    return selected.filter((item) => item.text.toLocaleLowerCase().includes(normalizedSearch));
  }, [selected, selectedSearch]);
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const updateSelection = (items) => {
    setSelected(items);
    syncMultiSelect(selectEl, items);
  };

  const addItem = (item) => {
    if (selectedIds.has(String(item.id))) {
      return;
    }

    const nextSelected = [...selected, item];
    updateSelection(nextSelected);
    setIsSelectionPanelOpen(true);
    window.requestAnimationFrame(() => {
      modalInputRef.current?.focus();
    });
  };

  const removeItem = (itemId) => {
    const nextSelected = selected.filter((item) => String(item.id) !== String(itemId));
    updateSelection(nextSelected);
  };

  const closeDetail = () => {
    setDetailState(null);
  };

  const openDetail = async (item, triggerEl) => {
    const itemId = String(item.id);
    detailTriggerRef.current = triggerEl instanceof HTMLElement ? triggerEl : null;

    const cachedDetail = detailCacheRef.current.get(itemId);
    if (cachedDetail) {
      setDetailState({
        status: 'ready',
        itemId,
        label: item.text,
        ...cachedDetail,
      });
      return;
    }

    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    setDetailState({
      status: 'loading',
      itemId,
      label: item.text,
      message: '',
      schemaUrl: '',
      pageUrl: '',
      fallbackUrl: '',
    });

    try {
      const params = new URLSearchParams({
        manager: config.manager,
        id: itemId,
      });
      const response = await window.fetch(
        `${config.baseUrl}cst-admin-tools/association-detail?${params}`,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        },
      );
      const data = await readApiPayload(response);

      if (detailRequestIdRef.current !== requestId) {
        return;
      }

      if (!response.ok || !data.ok || !data.detail) {
        throw new Error(data.message || 'Vue détail indisponible.');
      }

      const nextDetail = {
        schemaUrl: data.detail.reactSchemaUrl || deriveSchemaUrlFromReactPageUrl(data.detail.reactPageUrl),
        pageUrl: data.detail.reactPageUrl || '',
        fallbackUrl: data.detail.fallbackUrl || '',
      };

      detailCacheRef.current.set(itemId, nextDetail);
      setDetailState({
        status: 'ready',
        itemId,
        label: item.text,
        message: '',
        ...nextDetail,
      });
    } catch (error) {
      if (detailRequestIdRef.current !== requestId) {
        return;
      }

      setDetailState({
        status: 'error',
        itemId,
        label: item.text,
        message: error?.message || 'Vue détail indisponible.',
        schemaUrl: '',
        pageUrl: '',
        fallbackUrl: '',
      });
    }
  };

  const visibleResults = results.filter((item) => !selectedIds.has(String(item.id)));
  const selectedCountLabel = `${selected.length} ${selected.length > 1 ? 'entrées sélectionnées' : 'entrée sélectionnée'}`;
  const overlayTransition = shouldReduceMotion
    ? { duration: 0 }
    : { duration: 0.18, ease: [0.22, 1, 0.36, 1] };
  const panelTransition = shouldReduceMotion
    ? { duration: 0 }
    : { type: 'spring', stiffness: 380, damping: 34, mass: 0.85 };

  useEffect(() => {
    if (isFilterOpen) {
      setIsSelectionPanelOpen(true);
    } else {
      setSelectedSearch('');
    }
  }, [isFilterOpen]);

  useEffect(() => {
    if (!isAddModalOpen && !detailState) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (detailState) {
          closeDetail();
          return;
        }

        setIsAddModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [detailState, isAddModalOpen]);

  useEffect(() => {
    if (!isAddModalOpen) {
      setModalSearch('');
      return undefined;
    }

    window.requestAnimationFrame(() => {
      modalInputRef.current?.focus();
    });
  }, [isAddModalOpen]);

  useInfiniteScrollLoader({
    containerRef: modalResultsRef,
    sentinelRef: modalResultsSentinelRef,
    onLoadMore: loadMoreModal,
    enabled: isAddModalOpen && hasMoreModal,
  });

  useEffect(() => {
    if (detailState) {
      return undefined;
    }

    const trigger = detailTriggerRef.current;
    if (!(trigger instanceof HTMLElement)) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      trigger.focus();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [detailState]);

  return (
    <div className="ras-wrap ras-wrap--multi">
      <div className="ras-selected">
        <div className="ras-selected__toolbar">
          <button
            type="button"
            className="ras-selected__main"
            onClick={() => setIsSelectionPanelOpen((current) => !current)}
            aria-expanded={isSelectionPanelOpen}
          >
            <span className="ras-section-label ras-section-label--inline">Entrées</span>
            <span className="ras-selected__summary">{selectedCountLabel}</span>
            <span className={`ras-selected__chevron${isSelectionPanelOpen ? ' is-open' : ''}`} aria-hidden="true">
              ▾
            </span>
          </button>

          <div className="ras-selected__actions">
            {isSelectionPanelOpen && (
              <button
                type="button"
                className={`ras-icon-button${isFilterOpen ? ' is-active' : ''}`}
                aria-label="Rechercher dans les entrées sélectionnées"
                onClick={() => setIsFilterOpen((current) => !current)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className="ras-icon-button ras-icon-button--primary"
              aria-label="Ajouter une entrée"
              onClick={() => setIsAddModalOpen(true)}
            >
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <path
                  d="M9.25 4h1.5v5.25H16v1.5h-5.25V16h-1.5v-5.25H4v-1.5h5.25V4Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>

        {isFilterOpen && (
          <div className="ras-selected__search">
            <input
              type="text"
              className="ras-selected__search-input"
              value={selectedSearch}
              onChange={(event) => setSelectedSearch(event.target.value)}
              placeholder="Rechercher dans les entrées sélectionnées…"
            />
          </div>
        )}

        {isSelectionPanelOpen && (
          filteredSelected.length > 0 ? (
            <div className="ras-selected__grid">
              {filteredSelected.map((item) => (
                <article key={item.id} className="ras-card">
                  <div className="ras-card__header">
                    {(() => {
                      const { main, meta } = formatAssociationOption(item);
                      return (
                        <div className="ras-card__title" title={item.text}>
                          <span className="ras-card__title-main">{main}</span>
                          {meta && <span className="ras-card__title-meta">{meta}</span>}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="ras-card__actions">
                    <button
                      type="button"
                      className="ras-card__action"
                      aria-label={`Voir le détail de ${item.text}`}
                      title="Voir le détail"
                      disabled={detailState?.status === 'loading' && detailState.itemId === String(item.id)}
                      onClick={(event) => openDetail(item, event.currentTarget)}
                    >
                      <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                        <path
                          d="M10 4.5c-4.134 0-7.528 2.438-8.81 5.5c1.282 3.062 4.676 5.5 8.81 5.5s7.528-2.438 8.81-5.5C17.528 6.938 14.134 4.5 10 4.5Zm0 1.5c3.346 0 6.159 1.855 7.256 4c-1.097 2.145-3.91 4-7.256 4s-6.159-1.855-7.256-4c1.097-2.145 3.91-4 7.256-4Zm0 1.75A2.25 2.25 0 1 0 10 12.25A2.25 2.25 0 0 0 10 7.75Zm0 1.5a.75.75 0 1 1 0 1.5a.75.75 0 0 1 0-1.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="ras-card__action ras-card__action--danger"
                      aria-label={`Retirer ${item.text}`}
                      title="Retirer"
                      onClick={() => removeItem(item.id)}
                    >
                      <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                        <path
                          d="M7 3.75A1.75 1.75 0 0 1 8.75 2h2.5A1.75 1.75 0 0 1 13 3.75V4h3v1.5h-1v8.25A2.25 2.25 0 0 1 12.75 16h-5.5A2.25 2.25 0 0 1 5 13.75V5.5H4V4h3v-.25Zm1.5.25V4h3v-.25a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25ZM6.5 5.5v8.25c0 .414.336.75.75.75h5.5a.75.75 0 0 0 .75-.75V5.5h-7Zm2 1.75h1.5v5.5H8.5v-5.5Zm3 0H13v5.5h-1.5v-5.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="ras-selected__empty">
              {selected.length > 0
                ? 'Aucune entrée ne correspond à votre recherche.'
                : 'Aucune entrée sélectionnée.'}
            </div>
          )
        )}
      </div>

      {portalTarget ? createPortal(
        <>
          <AnimatePresence>
            {detailState ? (
              <div className="ras-modal-layer">
                <motion.button
                  key="ras-detail-backdrop"
                  type="button"
                  className="ras-modal__backdrop"
                  aria-label="Fermer le détail"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={overlayTransition}
                  onClick={closeDetail}
                />
                <motion.div
                  key="ras-detail-panel"
                  className="ras-modal ras-modal--detail ras-modal--side"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ras-detail-title"
                  initial={shouldReduceMotion ? { opacity: 0 } : { x: '100%' }}
                  animate={{ x: 0 }}
                  exit={shouldReduceMotion ? { opacity: 0 } : { x: '100%' }}
                  transition={shouldReduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 36, mass: 0.9 }}
                >
                  <div className="ras-modal__header">
                    <div>
                      <p className="ras-modal__eyebrow">Détail</p>
                      <h2 className="ras-modal__title" id="ras-detail-title">{detailState.label}</h2>
                    </div>
                    <div className="ras-modal__header-actions">
                      {detailState.pageUrl ? (
                        <a
                          className="ras-modal__close"
                          href={detailState.pageUrl}
                          aria-label="Plein écran"
                          title="Plein écran"
                        >
                          <ExpandIcon />
                          <span className="cat-react-visually-hidden">Plein écran</span>
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="ras-modal__close"
                        onClick={closeDetail}
                        aria-label="Fermer"
                        title="Fermer"
                      >
                        <CloseIcon />
                        <span className="cat-react-visually-hidden">Fermer</span>
                      </button>
                    </div>
                  </div>

                  <div className="ras-modal__body ras-modal__body--detail">
                    {detailState.status === 'loading' ? (
                      <div className="cat-react-state">Chargement du détail…</div>
                    ) : detailState.status === 'error' ? (
                      <div className="cat-react-state cat-react-state--error">{detailState.message}</div>
                    ) : (
                      <div className="ras-modal__detail-shell">
                        <RenderErrorBoundary message="Le détail React n'a pas pu être affiché.">
                          <App
                            key={detailState.schemaUrl || detailState.fallbackUrl || detailState.itemId}
                            settings={{
                              schemaUrl: detailState.schemaUrl,
                              layout: 'sidebar',
                              fallbackUrl: detailState.fallbackUrl,
                            }}
                          />
                        </RenderErrorBoundary>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            ) : null}
          </AnimatePresence>

          <AnimatePresence>
            {isAddModalOpen ? (
              <div className="ras-modal-layer">
                <motion.button
                  key="ras-modal-backdrop"
                  type="button"
                  className="ras-modal__backdrop"
                  aria-label="Fermer la recherche"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={overlayTransition}
                  onClick={() => setIsAddModalOpen(false)}
                />
                <motion.div
                  key="ras-modal-panel"
                  className="ras-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="ras-modal-title"
                  initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 18, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.99 }}
                  transition={panelTransition}
                >
                  <div className="ras-modal__header">
                    <div>
                      <p className="ras-modal__eyebrow">Ajouter</p>
                      <h2 className="ras-modal__title" id="ras-modal-title">Rechercher une entrée</h2>
                    </div>
                    <button
                      type="button"
                      className="ras-modal__close"
                      onClick={() => setIsAddModalOpen(false)}
                      aria-label="Fermer"
                      title="Fermer"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>

                  <div className="ras-modal__body">
                    <div className="ras-modal__search">
                      <input
                        ref={modalInputRef}
                        type="text"
                        className="ras-modal__search-input"
                        value={modalSearch}
                        onChange={(event) => setModalSearch(event.target.value)}
                        placeholder="Saisir un nom, un email ou un mot-clé…"
                      />
                    </div>

                    <div className="ras-results ras-results--modal" role="listbox" ref={modalResultsRef}>
                      {visibleResults.length === 0 && loading && <div className="ras-msg">Recherche…</div>}
                      {!loading && visibleResults.length === 0 && !hasMoreModal && <div className="ras-msg">Aucun résultat</div>}
                      {visibleResults.map((item) => (
                        <div
                          key={item.id}
                          className="ras-option"
                          role="option"
                          aria-selected={false}
                          onMouseDown={() => addItem(item)}
                        >
                          <AssociationOptionLabel item={item} />
                        </div>
                      ))}
                      {hasMoreModal && <div ref={modalResultsSentinelRef} className="ras-load-sentinel" aria-hidden="true" />}
                      {visibleResults.length > 0 && loading && <div className="ras-msg ras-msg--load-more">Chargement…</div>}
                    </div>
                  </div>
                </motion.div>
              </div>
            ) : null}
          </AnimatePresence>
        </>,
        portalTarget,
      ) : null}
    </div>
  );
}

export function ReactAjaxSelect({ selectEl }) {
  const mode = getAjaxSelectMode(selectEl);
  if (mode === 'multiple') {
    return <ReactAjaxMultiSelect selectEl={selectEl} />;
  }

  return <ReactAjaxSingleSelect selectEl={selectEl} />;
}

const reactSelectRoots = new WeakMap();

export function mountReactSelect(selectEl) {
  if (reactSelectRoots.has(selectEl)) return;
  selectEl.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none';
  const wrapper = document.createElement('div');
  wrapper.className = 'ras-host';
  selectEl.parentNode.insertBefore(wrapper, selectEl.nextSibling);
  const root = createRoot(wrapper);
  root.render(
    <RenderErrorBoundary message="ReactAjaxSelect failed.">
      <ReactAjaxSelect selectEl={selectEl} />
    </RenderErrorBoundary>,
  );
  reactSelectRoots.set(selectEl, root);
}
